import React from "react";
import { Form, Segmented, Select, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { branchAddress, findBranch, pickableBranches } from "./customerBranches";

const { Text } = Typography;

/**
 * Branch (Niederlassung) of the chosen customer for an invoice, offer or project, and who the
 * document is addressed to. Renders nothing when the customer has no branches.
 *
 * value: { clientBranch, billTo }; onChange receives the same shape.
 * Without showBillTo (e.g. an appointment) the branch is only a place, so the hints say so.
 * Without showBranchSelect the branch is picked in CustomerSelect; this then only shows where
 * the document goes and the addressee switch, and nothing while no branch is chosen.
 */
const CustomerBranchPicker = ({
  customer,
  value = {},
  onChange,
  disabled,
  showBillTo = true,
  showBranchSelect = true
}) => {
  const { t: ta } = useTranslation("accounting");
  const branches = pickableBranches(customer, value.clientBranch);
  if (!customer || !branches.length) return null;

  const branch = value.clientBranch ? findBranch(customer, value.clientBranch) : null;
  const billTo = value.billTo || "branch";
  const pick = id => {
    const next = id ? findBranch(customer, id) : null;
    onChange?.({ clientBranch: id || null, billTo: next?.defaultBillTo || "branch" });
  };

  const hint = !branch
    ? showBillTo
      ? ta("Documents go to the customer's main address")
      : ta("At the customer's main address")
    : showBillTo && billTo === "head_office"
      ? `${ta("Addressed to the head office; the branch is printed as place of service")}: ${branchAddress(branch) || "–"}`
      : `${branchAddress(branch) || ta("The branch has no address yet")}${branch.email ? ` · ${branch.email}` : ""}`;

  if (!showBranchSelect) {
    if (!branch) return null;
    return (
      <>
        {showBillTo ? (
          <Form.Item label={ta("Address the document to")}>
            <Segmented
              block
              disabled={disabled}
              value={billTo}
              onChange={v => onChange?.({ clientBranch: value.clientBranch, billTo: v })}
              options={[
                { value: "branch", label: ta("The branch") },
                { value: "head_office", label: ta("The head office") }
              ]}
            />
          </Form.Item>
        ) : null}
        <Text type="secondary" style={{ display: "block", marginTop: -16, marginBottom: 16 }}>
          {hint}
        </Text>
      </>
    );
  }

  return (
    <>
      <Form.Item label={ta("Branch")} help={<Text type="secondary">{hint}</Text>}>
        <Select
          size="large"
          allowClear
          showSearch
          disabled={disabled}
          value={value.clientBranch || undefined}
          placeholder={ta("Head office (no branch)")}
          onChange={pick}
          optionFilterProp="label"
          options={branches.map(b => ({
            value: String(b._id),
            label: [b.name, b.code, b.city].filter(Boolean).join(" · "),
            archived: b.active === false
          }))}
          optionRender={option => (
            <span>
              {option.data.label}
              {option.data.archived ? (
                <Tag style={{ marginInlineStart: 8 }}>{ta("Archived")}</Tag>
              ) : null}
            </span>
          )}
        />
      </Form.Item>
      {branch && showBillTo ? (
        <Form.Item label={ta("Address the document to")}>
          <Segmented
            block
            disabled={disabled}
            value={billTo}
            onChange={v => onChange?.({ clientBranch: value.clientBranch, billTo: v })}
            options={[
              { value: "branch", label: ta("The branch") },
              { value: "head_office", label: ta("The head office") }
            ]}
          />
        </Form.Item>
      ) : null}
    </>
  );
};

export default CustomerBranchPicker;
